import React, { useContext, useState } from 'react';

import PropTypes from 'prop-types';
import { Trans, plural, t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import { AuthStore } from 'stores';
import { ERRORS, PATHS } from 'config';
import * as help from 'helpers';

import {
    Form,
    FormField,
    FormInput,
    FormPasswordField,
    Card,
    TextSeparator,
    Loader,
    FacebookAccess,
    AppleSignInButton,
    AuthTerms,
    Alert,
    Button,
    Typography,
} from 'components';

import { StyledReactLink, CenteredDiv } from 'components/StyledCommon';

const LoginForm = (props) => {
    const {
        onSignupClick,
        downloadDataAfterLogin,
        onFacebookResponse,
        loginErrorMessage,
        emailAddress,
        showForgotLink,
        onSuccess,
        noPadding,
        showFooter,
        onLoginDestination,
        onForgotPasswordClick,
        logoutOnError,
    } = props;

    const auth = useContext(AuthStore);
    const submitting = help.useBoolean(false);
    const [error, setError] = useState('');

    const handleSubmitForm = (e) => {
        e && e.preventDefault();

        if (!validate() || submitting.value) {
            return;
        }

        submitting.setTrue();

        const data = getData();

        auth.login(data.username, data.password, downloadDataAfterLogin)
            .then(
                onSuccess
                    ? onSuccess
                    : async () => {
                          await auth.zendesk();
                          props.history.replace(onLoginDestination);
                      },
            )
            .catch((err) => {
                if (err.response && err.response.statusCode === 500) {
                    setError(i18n._(ERRORS.login_server_error));
                } else if (
                    err.response &&
                    err.response.body &&
                    err.response.body.wait_seconds
                ) {
                    const wait_seconds = Math.floor(
                        err.response.body.wait_seconds,
                    );
                    setError(
                        i18n._(
                            plural(wait_seconds, {
                                one:
                                    'Could not login due to multiple failed attempts. Please try again later in 1 second.',
                                other: `Could not login due to multiple failed attempts. Please try again later in ${wait_seconds} seconds.`,
                            }),
                        ),
                    );
                } else {
                    setError(
                        loginErrorMessage
                            ? loginErrorMessage
                            : i18n._(ERRORS.login_wrong_credentials),
                    );
                }

                if (logoutOnError) {
                    auth.logout();
                }

                submitting.setFalse();
            });
    };

    const onFacebookAccessSuccess = async () => {
        await auth.zendesk();
        props.history.replace(onLoginDestination);
    };

    const onFacebookAccessError = (err) => {
        submitting.setFalse();
        err.status === 404
            ? setError(i18n._(ERRORS.facebook_no_account))
            : setError(i18n._(ERRORS.facebook_unknown));
    };

    const formInitialData = {
        username: emailAddress ? emailAddress : '',
        password: '',
    };

    const formRules = {
        username: 'required',
        password: 'required',
    };

    const formCustomMessages = {
        'username.required': i18n._(t`This field is required`),
        'password.required': i18n._(t`This field is required`),
    };

    const { form, errors, validate, getData } = help.useValidatedForm(
        formInitialData,
        formRules,
        formCustomMessages,
    );

    return (
        <Card noPadding={noPadding} noShadow mobileCard>
            {submitting.value && (
                <Loader
                    absolute={true}
                    position="bottom"
                    data-testid="login-loader"
                />
            )}

            <FacebookAccess
                onResponse={onFacebookResponse}
                submitting={submitting.value}
                setSubmitting={submitting.setValue}
                onSuccess={onFacebookAccessSuccess}
                onError={onFacebookAccessError}
                isLogin={true}
                label={<Trans>Log in with Facebook</Trans>}
            />
            <AppleSignInButton />

            <TextSeparator>
                <Trans>or</Trans>
            </TextSeparator>

            {error && (
                <Alert plain align="center" icon="alert" color="error">
                    {error}
                </Alert>
            )}

            <Form onSubmit={handleSubmitForm}>
                {!emailAddress && (
                    <FormField
                        label={i18n._(t`Email address or username`)}
                        labelProps={{ htmlFor: 'form_email' }}
                        error={errors.username}
                    >
                        <FormInput
                            {...form.username.input}
                            disabled={submitting.value}
                            placeholder=""
                            id="form_email"
                            tabIndex={0}
                            autoComplete="username"
                            inputMode="email"
                        />
                    </FormField>
                )}

                <FormField
                    label={i18n._(t`Password`)}
                    labelProps={{ htmlFor: 'form_password' }}
                    error={errors.password}
                >
                    <FormPasswordField
                        {...form.password.input}
                        disabled={submitting.value}
                        id="form_password"
                        tabIndex={0}
                        autoComplete="current-password"
                    />
                </FormField>

                {showForgotLink && (
                    <CenteredDiv topMargin>
                        <StyledReactLink
                            onClick={
                                onForgotPasswordClick
                                    ? onForgotPasswordClick
                                    : null
                            }
                            to={help.route_with_params(PATHS.forgotPassword)}
                        >
                            <Trans>Forgot your password?</Trans>
                        </StyledReactLink>
                    </CenteredDiv>
                )}

                <CenteredDiv topMargin>
                    <Button
                        data-action="submit"
                        type="submit"
                        theme={!submitting.value ? 'primary' : 'inactive'}
                    >
                        <Trans>Log in</Trans>
                    </Button>
                </CenteredDiv>
            </Form>

            {showFooter && (
                <CenteredDiv>
                    <AuthTerms />

                    <TextSeparator margin="medium" />

                    <Typography variant="bodym">
                        <Trans>Don't have an account?</Trans>
                    </Typography>

                    <Button
                        spaceTop="base"
                        data-action="switch"
                        theme="secondary"
                        onClick={onSignupClick ? onSignupClick : null}
                        to={help.route_with_params(PATHS.register)}
                        history={props.history}
                    >
                        <Trans>Sign up</Trans>
                    </Button>
                </CenteredDiv>
            )}
        </Card>
    );
};

LoginForm.defaultProps = {
    onLoginDestination: PATHS.dashboard,
    showFooter: true,
    noPadding: false,
    onSuccess: null,
    showForgotLink: true,
    onFacebookResponse: null,
    downloadDataAfterLogin: true,
    logoutOnError: true,
};

LoginForm.propTypes = {
    onSignupClick: PropTypes.func,
    onFacebookResponse: PropTypes.func,
    onForgotPasswordClick: PropTypes.func,
    onLoginDestination: PropTypes.string,
    showFooter: PropTypes.bool,
    noPadding: PropTypes.bool,
    onSuccess: PropTypes.func,
    emailAddress: PropTypes.string,
    showForgotLink: PropTypes.bool,
    loginErrorMessage: PropTypes.string,
    downloadDataAfterLogin: PropTypes.bool,
};

export default LoginForm;
